import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";

/**
 * Status panel for the scheduled VectorSurv sync (B11).
 *
 * Deliberately the same shape as the verification watcher's panel: both
 * answer the same question about a job nobody can see, and two layouts for
 * one question means learning to read two things. Activity strip, live log,
 * detail, recipients -- in that order, there as here.
 *
 * Three independent signals, kept separate because they fail separately:
 *   - heartbeat: a 30s timer writing a timestamp. Proves the host and systemd
 *     are alive right now. Shared with the watcher; it is a property of the
 *     box, not of either job.
 *   - timer armed: systemd's own view of pioneer-sync.timer. A heartbeat
 *     cannot see that this particular timer was stopped, which would leave
 *     the dashboards going stale while everything else looked healthy.
 *   - runs: the sync itself. Proves VectorSurv, AGOL and the comparison all
 *     work, but is silent for half an hour at a time.
 */

const UPTIME_DAYS = 90;

interface LayerState {
    name: string;
    rows?: number;
    added?: number;
    updated?: number;
    deleted?: number;
    unchanged?: number;
    update_ratio?: number;
    suspicious?: boolean;
    error?: string;
    skipped?: string;
    sites_without_geography?: number;
}

type DayBucket = { runs: number; failed: number; changed: number };

type HistoryEntry = {
    at?: string;
    ok?: boolean;
    problems?: number;
    layers?: Record<string, { rows?: number; added?: number; updated?: number; deleted?: number }>;
};

type Heartbeat = {
    at?: string;
    boot?: string;
    age_seconds?: number;
    alive?: boolean;
    error?: string;
};

interface SyncStatus {
    installed: boolean;
    health: "healthy" | "stale" | "failing" | "churning" | "unknown";
    ok?: boolean;
    last_run_at?: string | null;
    last_run_local?: string | null;
    expected_by?: string | null;
    year?: number;
    dry_run?: boolean;
    layers?: Record<string, LayerState>;
    problems?: { title: string; detail: string }[];
    schedule?: string;
    message?: string;
    path?: string;
    heartbeat?: Heartbeat | null;
    timer?: { armed?: boolean; state?: string; next_run_local?: string | null } | null;
    history?: HistoryEntry[];
    daily?: Record<string, DayBucket>;
    recipients?: { to?: string[]; cc?: string[] };
}

const HEALTH: Record<string, { label: string; dot: string; text: string; blurb: string }> = {
    healthy: {
        label: "Active", dot: "bg-green-500", text: "text-green-700",
        blurb: "Running on schedule, and the layers match VectorSurv.",
    },
    stale: {
        label: "Stale", dot: "bg-amber-500", text: "text-amber-700",
        blurb: "A run was due and hasn't happened. The dashboards are showing whatever they held then.",
    },
    failing: {
        label: "Failing", dot: "bg-red-500", text: "text-red-700",
        blurb: "The last run reported errors. The layers hold the last good data — stale rather than wrong.",
    },
    churning: {
        label: "Rewriting everything", dot: "bg-amber-500", text: "text-amber-700",
        blurb: "Nearly every row changed, which usually means the comparison is faulty rather than the data.",
    },
    unknown: {
        label: "Not installed", dot: "bg-slate-400", text: "text-slate-600",
        blurb: "The scheduled job hasn't run on this host.",
    },
};

type LogLine = { key: string; at: string; kind: "beat" | "run" | "warn"; text: string };

function clockTime(iso: string) {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? "--:--:--"
        : d.toLocaleTimeString([], { hour12: false });
}

function dayKeys(days: number) {
    const out: string[] = [];
    const today = new Date();
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        out.push(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
        );
    }
    return out;
}

export default function VectorSurvSyncCard() {
    const [data, setData] = useState<SyncStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [lines, setLines] = useState<LogLine[]>([]);
    const logRef = useRef<HTMLDivElement | null>(null);
    const autoScroll = useRef(true);
    const seenRuns = useRef<Set<string>>(new Set());

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await api.get(`/sync-status?_=${Date.now()}`);
            const d: SyncStatus = r.data;
            setData(d);
            setError(null);

            // The log is built from what the poll returns rather than kept on
            // the server: a heartbeat line every tick, and a run line the
            // first time each run is seen. Runs are keyed by timestamp so a
            // poll that returns the same run twice does not repeat it.
            const next: LogLine[] = [];
            if (d.heartbeat?.at) {
                next.push({
                    key: `beat-${d.heartbeat.at}`,
                    at: d.heartbeat.at,
                    kind: "beat",
                    text: `heartbeat ok (age ${Math.round(d.heartbeat.age_seconds ?? 0)}s)`,
                });
            }
            for (const h of d.history ?? []) {
                if (!h.at || seenRuns.current.has(h.at)) continue;
                seenRuns.current.add(h.at);
                const moved = Object.values(h.layers ?? {}).reduce(
                    (n, l) => n + (l.added ?? 0) + (l.updated ?? 0) + (l.deleted ?? 0), 0);
                next.push({
                    key: `run-${h.at}`,
                    at: h.at,
                    kind: h.ok ? "run" : "warn",
                    text: h.ok
                        ? `sync ok — ${moved === 0 ? "no changes" : `${moved} row${moved === 1 ? "" : "s"} written`}`
                        : `sync reported problems`,
                });
            }
            if (next.length) {
                setLines((prev) =>
                    [...prev, ...next.filter((l) => !prev.some((p) => p.key === l.key))].slice(-200));
            }
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not read the sync status.");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        load();
        // Every 30s, to match the heartbeat it displays. The job itself runs
        // half-hourly, but the heartbeat is the thing that has to look live.
        const t = window.setInterval(load, 30000);
        return () => window.clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (autoScroll.current && logRef.current) {
            logRef.current.scrollTop = logRef.current.scrollHeight;
        }
    }, [lines]);

    const h = HEALTH[data?.health || "unknown"] || HEALTH.unknown;
    const layers = Object.entries(data?.layers || {});
    const daily = data?.daily || {};
    const series = dayKeys(UPTIME_DAYS).map((date) => ({ date, bucket: daily[date] }));
    const totalRuns = Object.values(daily).reduce((n, b) => n + b.runs, 0);
    const totalFailed = Object.values(daily).reduce((n, b) => n + b.failed, 0);
    const activeDays = Object.values(daily).filter((b) => b.runs > 0).length;
    const uptimePct = totalRuns === 0 ? null : ((totalRuns - totalFailed) / totalRuns) * 100;
    const beatAlive = !!data?.heartbeat?.alive;

    const barColour = (b?: DayBucket) => {
        if (!b || b.runs === 0) return "bg-slate-200";
        if (b.failed === 0) return "bg-green-500";
        if (b.failed >= b.runs) return "bg-red-500";
        return "bg-amber-400";
    };

    const lineColour = (k: LogLine["kind"]) =>
        k === "warn" ? "text-red-400" : k === "run" ? "text-sky-300" : "text-slate-400";

    return (
        <div className="space-y-5 text-sm">
            <p className="text-ink-soft text-[13px]">
                Pulls this year's collections, pools and surveillance sites from VectorSurv
                and brings the two dashboard layers into line with them. It adds and updates
                what changed rather than emptying the layers, so a run that fails leaves the
                dashboards holding the last good data.
            </p>

            {/* ---- Uptime bar ---- */}
            <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-baseline justify-between mb-2">
                    <span className="text-xs text-slate-500">
                        Sync runs over the past {UPTIME_DAYS} days
                    </span>
                    <span className={`text-xs font-semibold ${h.text}`}>{h.label}</span>
                </div>

                <div className="flex items-end gap-[2px] h-10">
                    {series.map((d) => (
                        <div
                            key={d.date}
                            title={
                                d.bucket
                                    ? `${d.date}: ${d.bucket.runs} runs, ${d.bucket.failed} failed, ${d.bucket.changed} row(s) written`
                                    : `${d.date}: no runs (weekend, Monday, or before install)`
                            }
                            className={`flex-1 min-w-[3px] h-full rounded-sm ${barColour(d.bucket)}`}
                        />
                    ))}
                </div>

                <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400">
                    <span>{UPTIME_DAYS} days ago</span>
                    <span className="text-slate-600">
                        {uptimePct === null
                            ? "no data yet"
                            : `${uptimePct.toFixed(1)} % of ${totalRuns} runs succeeded`}
                        <span className="text-slate-400"> · {activeDays} active day{activeDays === 1 ? "" : "s"}</span>
                    </span>
                    <span>Today</span>
                </div>
            </div>

            {/* ---- Live log ---- */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">Live activity</span>
                    <span className="flex items-center gap-1.5 text-xs">
                        <span
                            className={`inline-block h-2 w-2 rounded-full ${beatAlive ? "bg-green-500 animate-pulse" : "bg-red-500"}`}
                        />
                        <span className={beatAlive ? "text-green-700" : "text-red-700"}>
                            {beatAlive ? "heartbeat live" : "no heartbeat"}
                        </span>
                    </span>
                </div>

                <div
                    ref={logRef}
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        autoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
                    }}
                    className="h-40 overflow-y-auto rounded-lg bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed"
                >
                    {lines.length === 0 ? (
                        <div className="text-slate-500">waiting for the next heartbeat…</div>
                    ) : (
                        lines.map((l) => (
                            <div key={l.key} className={lineColour(l.kind)}>
                                <span className="text-slate-600">{clockTime(l.at)}</span>{" "}
                                {l.kind === "beat" ? "·" : l.kind === "warn" ? "!" : "▸"} {l.text}
                            </div>
                        ))
                    )}
                </div>

                <div className="mt-1 text-[11px] text-slate-400">
                    Heartbeat every 30s proves the server and its timers are alive. The sync
                    itself runs every half hour on working days — that's the line that proves
                    VectorSurv, AGOL and the comparison are all working.
                </div>
            </div>

            {/* ---- Detail ---- */}
            <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${h.dot}`} />
                <span className={`font-semibold ${h.text}`}>{h.label}</span>
                <span className="text-slate-500">— {h.blurb}</span>
                {data?.dry_run && <span className="pill bg-paper-2 text-ink-faint">last run was a dry run</span>}
                <button onClick={load} disabled={busy} className="btn-ghost text-xs !py-1 ml-auto">
                    {busy ? "Reading…" : "Refresh"}
                </button>
            </div>

            {error && <p className="text-[13px] text-wnv">{error}</p>}

            {data?.timer && data.timer.armed === false && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
                    <div className="font-medium">The timer is not armed</div>
                    <div className="text-xs">
                        systemd reports pioneer-sync.timer as {data.timer.state || "inactive"}. Nothing
                        will run until it is started, however healthy everything else looks.
                    </div>
                </div>
            )}

            {data?.installed === false && (
                <p className="text-[12px] text-ink-faint">
                    {data.message} Until the timer is installed, the dashboards update
                    only when someone presses <strong>Sync AGOL</strong> in the two
                    dashboard tools — which still works and is unaffected by this.
                </p>
            )}

            {data?.installed && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <Fact label="Last run" value={data.last_run_local || "—"} />
                    <Fact label="Next run" value={data.timer?.next_run_local || "—"} />
                    <Fact label="Was due by" value={data.expected_by || "—"} />
                    <Fact label="Season" value={data.year ? String(data.year) : "—"} />
                    <Fact label="Schedule" value={data.schedule || "—"} />
                    <Fact
                        label="Server up since"
                        value={data.heartbeat?.boot ? data.heartbeat.boot.replace("T", " ").slice(0, 19) : "—"}
                    />
                </div>
            )}

            {!!data?.problems?.length && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
                    <div className="font-medium">Needs attention</div>
                    {data.problems.map((p, i) => (
                        <div key={i} className="mt-1">
                            <div className="font-medium text-[13px]">{p.title}</div>
                            <div className="text-xs">{p.detail}</div>
                        </div>
                    ))}
                </div>
            )}

            {layers.length > 0 && (
                <div>
                    <div className="text-xs text-slate-500 mb-1">What the last run did</div>
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <table className="w-full text-[13px]">
                            <thead className="bg-paper-2">
                                <tr>
                                    {["Layer", "Rows", "Added", "Changed", "Removed", "Untouched"].map((x) => (
                                        <th key={x} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{x}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {layers.map(([key, l]) => (
                                    <tr key={key} className="border-t border-slate-200">
                                        <td className="px-3 py-2 font-semibold">{l.name}</td>
                                        <td className="px-3 py-2">{l.rows?.toLocaleString() ?? "—"}</td>
                                        <td className="px-3 py-2">{l.added ?? (l.skipped ? "—" : 0)}</td>
                                        <td className={`px-3 py-2 ${l.suspicious ? "text-wnv font-bold" : ""}`}>
                                            {l.updated ?? (l.skipped ? "—" : 0)}
                                        </td>
                                        <td className="px-3 py-2">{l.deleted ?? (l.skipped ? "—" : 0)}</td>
                                        <td className="px-3 py-2 text-slate-400">{l.unchanged?.toLocaleString() ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-400">
                        A normal run adds a few rows and leaves the rest alone. Large numbers
                        in <em>changed</em> are worth a look: it usually means the comparison
                        is wrong rather than that much data moved.
                    </div>
                    {layers.some(([, l]) => l.error) && (
                        <div className="mt-2 rounded bg-red-50 border border-red-200 p-2 text-red-800">
                            {layers.filter(([, l]) => l.error).map(([key, l]) => (
                                <p key={key} className="text-xs">
                                    <strong>{l.name}:</strong> {l.error}
                                </p>
                            ))}
                        </div>
                    )}
                    {layers.some(([, l]) => (l.sites_without_geography ?? 0) > 0) && (
                        <div className="mt-1 text-[11px] text-slate-400">
                            Some sites have no geography yet — they are new to VectorSurv and
                            have never appeared in a manual export, which is where those
                            columns come from. They sync with those columns empty rather than
                            with a guess.
                        </div>
                    )}
                </div>
            )}

            {data?.recipients?.to?.length ? (
                <div className="text-xs">
                    <div className="text-slate-500">Alerts go to</div>
                    <div className="font-medium">{data.recipients.to.join(", ")}</div>
                    {data.recipients.cc?.length ? (
                        <div className="text-slate-500 text-xs">cc {data.recipients.cc.join(", ")}</div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function Fact({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <div className="text-slate-500 text-xs">{label}</div>
            <div className="font-medium">{value}</div>
        </div>
    );
}
